import React, { useEffect, useMemo, useState } from "react";
import { FaArrowRight, FaPrint, FaThumbsUp, FaTriangleExclamation } from "react-icons/fa6";
import "./direct-bill.style.scss";
import { useNavigate } from "react-router-dom";

// for date purpose
import moment from "moment";

// components
import InvoiceForm from "../invoice-form/invoice-form.component";
import type { InvoiceFormValues } from "../invoice-form/invoice-form.types";
import BillSummaryCard from "../bill-summary-card/bill-summary-card.component";
import PrintOptionsDialog from "../print-options-dialog/print-options-dialog.component";
import InlineNotice from "../inline-notice/inline-notice.component";

// global functions
import { fromAdToBs, isEmptyObject } from "../../global/function";
import { isChargeModeActive } from "../../global/billing-charge.util";

// queries
import { useQueryClient } from "@tanstack/react-query";
import { useCompanyDetails, useActiveExternalApp } from "../../queries/company.queries";
import {
	useBillDates,
	billDatesQueryKey,
	useAddBill,
	useIncrementPrintCount,
	usePrintBill,
	handleBillSubmitError,
	recordedByName,
} from "../../queries/bill.queries";
import type { BillDatesResult, PrintSize } from "../../queries/bill.queries";
import { useLastPrintSize } from "../../hooks/useLastPrintSize";

type DraftFormValues = Partial<InvoiceFormValues> & Record<string, unknown>;

interface DirectBillProps {
	// Reports live dirty state up to the parent, which owns the
	// StructuredSideModal this renders inside — lets it show a
	// discard-changes confirm before closing (see dasboard.page.tsx).
	onDirtyChange?: (isDirty: boolean) => void;
}

function DirectBill({ onDirtyChange }: DirectBillProps) {
	const { data: companyData } = useCompanyDetails();
	const preferenceDetails = companyData?.company?.preference;
	const { data: billDates } = useBillDates();
	const lastBillDate = billDates?.lastInvoiceDate ?? "";
	const { billModel, systemType } = useActiveExternalApp();
	const isCreationBlocked = billModel === "A";
	const connectedAppName = systemType ? systemType.toUpperCase() : "the connected app";

	const [billPreview, setBillPreview] = useState(false);
	const [printDialogOpen, setPrintDialogOpen] = useState(false);
	const [printSuccessNotice, setPrintSuccessNotice] = useState<string | null>(null);
	const [unsavedChanges, setUnsavedChanges] = useState<DraftFormValues>({});

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const addBillMutation = useAddBill();
	const incrementPrintCountMutation = useIncrementPrintCount();
	const printBillMutation = usePrintBill();
	const [lastPrintSize, setLastPrintSize] = useLastPrintSize();

	// addBillMutation already tracks the added bill (data) and outcome
	// (isSuccess/isError/reset) — no need to mirror either into local state.
	const addedBill = addBillMutation.data;
	const billResponse: "success" | "failed" | null = addBillMutation.isSuccess
		? "success"
		: addBillMutation.isError
			? "failed"
			: null;

	// Nothing left to lose once the bill has actually been created — avoids
	// a stale discard-changes prompt after a successful submit.
	const isDirty = billResponse !== "success" && !isEmptyObject(unsavedChanges);
	useEffect(() => {
		onDirtyChange?.(isDirty);
	}, [isDirty, onDirtyChange]);

	const handleCreateBill = async (billData: DraftFormValues, saveTo: string) => {
		if (saveTo !== "bill") return;
		try {
			await addBillMutation.mutateAsync({
				bills: [billData as Record<string, unknown>],
			});
			// Jump straight to the generated bill instead of leaving the
			// just-submitted (and now stale) InvoiceForm on screen behind the
			// success banner — it never resets on its own since currentDraft
			// here is a fixed template, not something this component clears.
			setBillPreview(true);
			queryClient.setQueryData<BillDatesResult | undefined>(
				billDatesQueryKey,
				(old) => old && { ...old, lastInvoiceDate: billData?.invoice_date as string },
			);
			// useAddBill has no onSuccess of its own (unlike useAddCreditNote) —
			// without this the bills list page can still show its pre-creation
			// cache after navigating here from elsewhere if that cache is under
			// staleTime old.
			queryClient.invalidateQueries({ queryKey: ["bills"] });
		} catch (error) {
			handleBillSubmitError(error, navigate);
		}
	};

	// print new bill: fetch the BE-rendered PDF (opens in a new tab), then
	// bump print_copies the same way the old ReactToPrint onAfterPrint did.
	const handlePrintConfirm = async (size: PrintSize) => {
		if (!addedBill?.bill_id) return;
		try {
			await printBillMutation.mutateAsync({ billId: addedBill.bill_id, size });
			await incrementPrintCountMutation.mutateAsync({
				updates: [{ id: addedBill.bill_id, model_type: "bill", print_type: "print" }],
			});
			setLastPrintSize(size);
			setPrintDialogOpen(false);
			setPrintSuccessNotice("PDF opened in a new tab.");
		} catch {
			// PrintOptionsDialog's own FormError (bound to printBillMutation) shows the message.
		}
	};

	// to populate data in the invoice form
	const currentInvoiceData: DraftFormValues = useMemo(
		() => ({
			invoice_date: moment().format("YYYY-MM-DD"),
			billed_items: [
				{
					item_name: "",
					hs_code: "",
					item_price: 0,
					item_qty: 0,
					total_price: 0,
					vatable: isChargeModeActive(preferenceDetails),
				},
			],
		}),
		[preferenceDetails],
	);

	// date show
	const last_bill_date = useMemo(
		() =>
			lastBillDate !== "" &&
			`${fromAdToBs(lastBillDate, "DD MMMM, YYYY")} | ${moment(
				lastBillDate,
			).format("DD MMMM YYYY")}`,
		[lastBillDate],
	);

	return (
		<div className="create-invoice-modal">
			{billResponse === "success" && billPreview && (
				<div className="action-container" style={{ padding: "8px 16px 0" }}>
					<div className="action-btn inverted" onClick={() => setPrintDialogOpen(true)}>
						<FaPrint />
						Print
					</div>
					<div className="action-btn" onClick={() => navigate("/invoice/bills")}>
						View All Bills
					</div>
				</div>
			)}
			<InlineNotice
				message={printSuccessNotice}
				type="success"
				autoHideMs={4000}
				onDismiss={() => setPrintSuccessNotice(null)}
			/>
			<div className="modal-body">
				{billResponse && (
					<div className={`bill-add-response ${billResponse}`}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "16px",
							}}
						>
							<div className="response-icon">
								{billResponse === "success" && (
									<FaThumbsUp />
								)}
								{billResponse === "failed" && (
									<FaTriangleExclamation />
								)}
							</div>
							<div className="response-message">
								{billResponse === "success" &&
									"Success! Bill generated successfully"}
								{billResponse === "failed" &&
									"Failed! Bill generation failure. Try again"}
							</div>
						</div>
						{billResponse === "success" && (
							<>
								{billPreview ? (
									<div
										className="response-action-btn"
										onClick={() => {
											setBillPreview(false);
											addBillMutation.reset();
										}}
									>
										Generate New Bill
										<FaArrowRight />
									</div>
								) : (
									<div
										className="response-action-btn"
										onClick={() => setBillPreview(true)}
									>
										View Generated Bill
										<FaArrowRight />
									</div>
								)}
							</>
						)}
					</div>
				)}

				{billPreview ? (
					<div className="modal-template-wrapper">
						{addedBill && (
							<div className="modal-template-container">
								<div className="modal-template-container-inner">
									<BillSummaryCard
										documentLabel={`Invoice #${addedBill.invoice_number}`}
										date={addedBill.invoice_date}
										dateNepali={addedBill.invoice_date_nepali}
										salesDate={addedBill.sales_date}
										buyerName={addedBill.buyer_name ?? ""}
										buyerPan={addedBill.buyer_pan}
										customerMobile={addedBill.customer_mobile ?? addedBill.customer_info?.customer_mobile}
										paymentMode={addedBill.payment_mode}
										recordedBy={recordedByName(addedBill.company_user)}
										printCopies={addedBill.print_copies}
										lastPrintDate={addedBill.lastPrintDate}
										billedItems={addedBill.billed_items ?? []}
										subTotal={addedBill.sub_total ?? 0}
										totalSales={addedBill.total_sales ?? 0}
										discount={(addedBill.non_taxable_discount ?? 0) + (addedBill.taxable_discount ?? 0)}
										serviceCharge={
											(addedBill.non_taxable_service_charge ?? 0) + (addedBill.taxable_service_charge ?? 0)
										}
										nonTaxableAmount={addedBill.non_taxable_amt}
										taxableAmount={addedBill.esf ? addedBill.amount_for_esf : addedBill.taxable_sales_vat}
										vat={addedBill.vat}
										esf={addedBill.esf}
										irdBillStatus={addedBill.ird_bill}
										billMeta={addedBill.bill_meta}
									/>
								</div>
							</div>
						)}
					</div>
				) : (
					<div style={{ padding: "16px" }}>
						<div className="invoice-form-header">
							<h2 className="form-header-text">
								Create Bill Invoice
							</h2>
							{lastBillDate !== "" && (
								<div className="form-header-subtext">
									<div className="label">Last Bill Date:</div>
									<div className="label-info">
										{last_bill_date}
									</div>
								</div>
							)}
						</div>
						<div className="header-divider"></div>
						{isCreationBlocked ? (
							<InlineNotice
								message={`Bill creation is disabled — ${connectedAppName} manages billing for this company. Bills pushed from ${connectedAppName} will appear in your bill list automatically.`}
								type="warning"
							/>
						) : (
							<InvoiceForm
								formType="individual"
								draftSave={handleCreateBill}
								currentDraft={currentInvoiceData}
								onSetCurrent={() => {}}
								onUnsavedChanges={setUnsavedChanges}
							/>
						)}
					</div>
				)}
			</div>
			<PrintOptionsDialog
				open={printDialogOpen}
				onOpenChange={setPrintDialogOpen}
				initialSize={lastPrintSize}
				isPending={printBillMutation.isPending}
				isError={printBillMutation.isError}
				error={printBillMutation.error}
				onConfirm={handlePrintConfirm}
			/>
		</div>
	);
}

export default DirectBill;
